import {SortableContext, verticalListSortingStrategy} from '@dnd-kit/sortable'
import {Plus, LayoutDashboard} from 'lucide-react'
import {useI18n} from '@/i18n'
import {Button} from '@/components/ui/button'
import {TooltipProvider} from '@/components/ui/tooltip'
import {cn} from '@/lib/utils'
import {SectionCard} from './SectionCard'
import type {DesignerAction, DesignerSelection, LayoutSchema, Palette} from '@/types/form-layout'

// ============================================================================
// Canvas empty state
// ============================================================================

function CanvasEmptyState({onAddSection}: {onAddSection: () => void}) {
    const i18n = useI18n()
    return (
        <div className="flex flex-col items-center justify-center py-20 text-center">
            <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full border-2 border-dashed border-muted-foreground/30">
                <LayoutDashboard className="h-8 w-8 text-muted-foreground/50" />
            </div>
            <h2 className="mb-2 text-lg font-semibold" data-testid="canvas-empty-title">{i18n.formLayouts.canvasEmpty}</h2>
            <p className="mb-6 max-w-sm text-sm text-muted-foreground">{i18n.formLayouts.canvasEmptySubtext}</p>
            <Button data-testid="canvas-add-section-btn" onClick={onAddSection}>
                <Plus className="mr-2 h-4 w-4" />
                {i18n.formLayouts.addSection}
            </Button>
        </div>
    )
}

// ============================================================================
// LayoutCanvas
// ============================================================================

interface LayoutCanvasProps {
    schema: LayoutSchema
    dispatch: React.Dispatch<DesignerAction>
    selection: DesignerSelection
    onSelect: (sel: DesignerSelection) => void
    palette: Palette | null
}

export function LayoutCanvas({schema, dispatch, selection, onSelect, palette}: LayoutCanvasProps) {
    const i18n = useI18n()

    // eslint-disable-next-line i18next/no-literal-string
    const sectionIds = schema.sections.map((s) => `section-${s.key}`)

    return (
        <TooltipProvider>
            {schema.sections.length === 0 ? (
                <CanvasEmptyState onAddSection={() => dispatch({type: 'ADD_SECTION'})} />
            ) : (
                <div className="space-y-4">
                    <SortableContext items={sectionIds} strategy={verticalListSortingStrategy}>
                        {schema.sections.map((section, sectionIndex) => (
                            <SectionCard
                                key={section.key}
                                section={section}
                                sectionIndex={sectionIndex}
                                dispatch={dispatch}
                                selection={selection}
                                onSelect={onSelect}
                                palette={palette}
                            />
                        ))}
                    </SortableContext>

                    {/* Add section button */}
                    <div className="flex justify-center pt-2">
                        <Button
                            data-testid="canvas-add-section-more-btn"
                            variant="outline"
                            size="sm"
                            onClick={() => dispatch({type: 'ADD_SECTION'})}
                            className={cn('border-dashed')}
                        >
                            <Plus className="mr-2 h-4 w-4" />
                            {i18n.formLayouts.addSection}
                        </Button>
                    </div>
                </div>
            )}
        </TooltipProvider>
    )
}
